import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ArrowRightOutlined,
  CloseOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Button, Modal } from "antd";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

import CheckoutProcessingOverlay from "../../components/CheckoutProcessingOverlay";
import SuccessModal from "../../components/SuccessModal";
import useGyngerCheckoutPayment, {
  GYNGER_CHECKOUT_PENDING_MESSAGE,
  GYNGER_SUCCESS_MODAL_AUTO_CLOSE_MS,
  PAYMENT_PERMISSION_DENIED_MESSAGE,
} from "../../hooks/useGyngerCheckoutPayment";
import { REVIEW_TOP_UP_CATEGORIES } from "../constants";
import ReviewTopUpBreakdown from "./ReviewTopUpBreakdown";
import ReviewTopUpPaymentSummary from "./ReviewTopUpPaymentSummary";

import { canMakePayment } from "../../../../../utils/Permissions";
import {
  buildCheckoutCreditsFromReviewCategories,
  formatCurrency,
} from "../../utils";

import {
  fetchUsageOutstanding,
  topupUsageCredits,
} from "../../../../../actions/usage";

import "./ReviewOneTimeTopUpModal.css";

const VARIANT_COPY = {
  topup: {
    summaryNote: "This purchase does not affect your recurring plan.",
    confirmLabel: (amount) => `Pay ${formatCurrency(amount)}`,
  },
  subscription: {
    summaryNote: "New amount will be added to the current billing",
    confirmLabel: (amount) => `Update amount to ${formatCurrency(amount)}`,
  },
};

function summarizeCategories(categories) {
  return categories.reduce((summary, category) => {
    category.items.forEach((item) => {
      summary.totalCredits += item.quantity;
      summary.finalAmount += item.quantity * item.rate;
    });
    return summary;
  }, { totalCredits: 0, finalAmount: 0 });
}

export default function ReviewOneTimeTopUpModal({
  open,
  onClose,
  onPay,
  payLoading = false,
  variant = "topup",
  categories = REVIEW_TOP_UP_CATEGORIES,
  totalCredits,
  finalAmount,
  currentMonthlyBill = 0,
  credits = [],
  savings = 120,
  offersUnlocked = 2,
  title = "Review One Time Top-Up",
  subtitle = "You're buying these credits as a one time purchase.",
}) {
  const [outstandingAmount, setOutstandingAmount] = useState(null);
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const [outstandingFailed, setOutstandingFailed] = useState(false);
  const [subscriptionSubmitting, setSubscriptionSubmitting] = useState(false);
  const [showSubscriptionSuccessModal, setShowSubscriptionSuccessModal] = useState(false);
  const idempotencyKeyRef = useRef(null);
  const idempotencyPayloadRef = useRef(null);
  const subscriptionSuccessResponseRef = useRef(null);
  const subscriptionSuccessCloseHandledRef = useRef(false);

  const handleCheckoutComplete = useCallback(async (response) => {
    if (onPay) {
      await onPay(response);
    }

    onClose();
  }, [onClose, onPay]);

  const {
    isCheckoutLoading,
    paymentUrl,
    showSuccessModal,
    handleSuccessClose,
    showFailedModal,
    handleFailedClose,
    startGyngerCheckout,
    resetCheckout,
  } = useGyngerCheckoutPayment({
    onComplete: handleCheckoutComplete,
  });

  const computed = useMemo(() => summarizeCategories(categories), [categories]);
  const displayCredits = totalCredits ?? computed.totalCredits;
  const displayAmount = finalAmount ?? computed.finalAmount;
  const isSubscription = variant === "subscription";
  const resolvedCurrentMonthlyBill = outstandingAmount ?? currentMonthlyBill;
  const newMonthlyBill = resolvedCurrentMonthlyBill + displayAmount;
  const copy = VARIANT_COPY[variant] || VARIANT_COPY.topup;
  const confirmLabel = copy.confirmLabel(
    isSubscription ? newMonthlyBill : displayAmount,
  );

  const checkoutCredits = useMemo(() => (
    credits.length > 0
      ? credits
      : buildCheckoutCreditsFromReviewCategories(categories)
  ), [credits, categories]);

  useEffect(() => {
    if (!open) {
      resetCheckout();
      setShowSubscriptionSuccessModal(false);
      subscriptionSuccessResponseRef.current = null;
      subscriptionSuccessCloseHandledRef.current = false;
    }
  }, [open, resetCheckout]);

  useEffect(() => {
    if (!open || !isSubscription) {
      return undefined;
    }

    const controller = new AbortController();
    let isMounted = true;

    const loadOutstanding = async () => {
      setOutstandingLoading(true);
      setOutstandingFailed(false);
      setOutstandingAmount(null);

      const response = await fetchUsageOutstanding({
        signal: controller.signal,
      });

      if (!isMounted || response.canceled) {
        return;
      }

      setOutstandingLoading(false);

      if (!response.success) {
        toast.error(response.message || "Unable to load outstanding amount.");
        setOutstandingFailed(true);
        setOutstandingAmount(currentMonthlyBill);
        return;
      }

      setOutstandingAmount(Number(response.result?.outstanding) || 0);
    };

    loadOutstanding();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [open, isSubscription, currentMonthlyBill]);

  const isPayLoading = payLoading || isCheckoutLoading || subscriptionSubmitting;
  const isInteractionLocked = isPayLoading || showSubscriptionSuccessModal;
  const showProcessingOverlay = isPayLoading && !isSubscription;
  const processingMessage = isCheckoutLoading
    ? GYNGER_CHECKOUT_PENDING_MESSAGE
    : "We’re processing your request. Please wait a moment.";

  const handleCancelPayment = () => {
    resetCheckout();
    setSubscriptionSubmitting(false);
  };

  const handleSubscriptionSuccessClose = useCallback(async () => {
    if (subscriptionSuccessCloseHandledRef.current) {
      return;
    }

    subscriptionSuccessCloseHandledRef.current = true;
    const response = subscriptionSuccessResponseRef.current;
    subscriptionSuccessResponseRef.current = null;
    setShowSubscriptionSuccessModal(false);

    if (onPay && response) {
      await onPay(response);
    }

    onClose();
  }, [onClose, onPay]);

  useEffect(() => {
    if (!showSubscriptionSuccessModal) {
      return undefined;
    }

    const timerId = setTimeout(() => {
      handleSubscriptionSuccessClose();
    }, GYNGER_SUCCESS_MODAL_AUTO_CLOSE_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [handleSubscriptionSuccessClose, showSubscriptionSuccessModal]);

  const handlePayClick = async () => {
    if (!canMakePayment()) {
      toast.error(PAYMENT_PERMISSION_DENIED_MESSAGE);
      return;
    }

    if (isSubscription && outstandingFailed) {
      return;
    }

    const useGyngerCheckout = variant === "topup";

    if (useGyngerCheckout) {
      await startGyngerCheckout({
        amount: displayAmount,
        credits: checkoutCredits,
      });
      return;
    }

    if (!Array.isArray(credits) || credits.length === 0) {
      toast.error("Select at least one test quantity to continue.");
      return;
    }

    const payloadSignature = JSON.stringify({ credits, amount: displayAmount });
    if (!idempotencyKeyRef.current || idempotencyPayloadRef.current !== payloadSignature) {
      idempotencyKeyRef.current = uuidv4();
      idempotencyPayloadRef.current = payloadSignature;
    }

    setSubscriptionSubmitting(true);
    const response = await topupUsageCredits({
      credits,
      amount: displayAmount,
      idempotencyKey: idempotencyKeyRef.current,
    });
    setSubscriptionSubmitting(false);

    if (response.canceled) {
      return;
    }

    if (!response.success) {
      toast.error(response.message || "Unable to update subscription.");
      return;
    }

    idempotencyKeyRef.current = null;
    idempotencyPayloadRef.current = null;
    subscriptionSuccessResponseRef.current = response;
    subscriptionSuccessCloseHandledRef.current = false;
    setShowSubscriptionSuccessModal(true);
  };

  return (
    <>
      <Modal
        open={open}
        onCancel={isInteractionLocked ? undefined : onClose}
        footer={null}
        closable={false}
        maskClosable={!isInteractionLocked}
        keyboard={!isInteractionLocked}
        centered
        destroyOnHidden
        width={960}
        className={`ReviewOneTimeTopUpModal${isSubscription ? " ReviewOneTimeTopUpModal--subscription" : ""}`}
      >
        <header className="ReviewOneTimeTopUpModal__Header">
          <div className="ReviewOneTimeTopUpModal__Heading">
            <span className="ReviewOneTimeTopUpModal__Accent" aria-hidden />
            <div>
              <h2 className="ReviewOneTimeTopUpModal__Title">{!isSubscription ? title : "Review updated current billing amount"}</h2>
              <p className="ReviewOneTimeTopUpModal__Subtitle">{subtitle}</p>
            </div>
          </div>
          {!isInteractionLocked && (
            <button
              type="button"
              className="ReviewOneTimeTopUpModal__CloseButton"
              onClick={onClose}
              disabled={isInteractionLocked}
              aria-label="Close review top-up modal"
            >
              <CloseOutlined />
            </button>
          )}
        </header>

        <div className="ReviewOneTimeTopUpModal__Body">
          <ReviewTopUpBreakdown categories={categories} />

          <ReviewTopUpPaymentSummary
            variant={variant}
            displayCredits={displayCredits}
            displayAmount={displayAmount}
            currentMonthlyBill={resolvedCurrentMonthlyBill}
            currentMonthlyBillLoading={outstandingLoading}
            addedTestsPerMonth={displayCredits}
            newMonthlyBill={newMonthlyBill}
            savings={savings}
            offersUnlocked={offersUnlocked}
            summaryNote={copy.summaryNote}
          />
        </div>

        <footer className="ReviewOneTimeTopUpModal__Footer">
          <p className="ReviewOneTimeTopUpModal__FooterNote">
            <InfoCircleOutlined aria-hidden />
            Credits are added immediately after the payment.
          </p>

          <div className="ReviewOneTimeTopUpModal__FooterActions">
            <Button
              type="default"
              onClick={onClose}
              disabled={isInteractionLocked}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              onClick={handlePayClick}
              loading={isSubscription && isPayLoading}
              disabled={isInteractionLocked || (isSubscription && (outstandingLoading || outstandingFailed))}
            >
              {confirmLabel}
            </Button>
          </div>
        </footer>

        <CheckoutProcessingOverlay
          open={showProcessingOverlay}
          message={processingMessage}
          onCancelPayment={handleCancelPayment}
          paymentUrl={paymentUrl}
        />
      </Modal>

      <SuccessModal
        open={showSuccessModal || showSubscriptionSuccessModal}
        onClose={showSubscriptionSuccessModal
          ? handleSubscriptionSuccessClose
          : handleSuccessClose}
        title={showSubscriptionSuccessModal
          ? "Subscription Updated Successfully!"
          : "Payment Successful!"}
        description={showSubscriptionSuccessModal
          ? "Test credits have been added to your subscription plan."
          : `${Number(displayCredits || 0).toLocaleString()} test credits have been added to your account.`}
      />
      <SuccessModal
        open={showFailedModal}
        onClose={handleFailedClose}
        variant="failed"
        title="Payment Failed"
        description="Your payment could not be completed. Please try again."
      />
    </>
  );
}
